import React, { useRef, useState, useEffect } from 'react';
import { isEqual, get } from 'lodash';
import { useTheme, ITheme } from 'src/theme';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import IconDown from '@assets/icon/chevron-down-white.svg';
import IconUp from '@assets/icon/chevron-up-white.svg';
import Heading from '@components/Heading';
import Text from '@components/Text';
import Link from '@components/Link';
import { Helpers } from '@utils/helpers';

const TotalContainer = styled.div<StyledProps>`
  display: flex;
  justify-content: center;
  align-items: baseline;
  margin-bottom: ${props => props.isOpen ? 20 : 0}px;
  .title-text {
    white-space: nowrap;
    @media screen and (min-width: 768px) {
      font-weight: 300;
    }
  }
`

const ToggleContainer = styled.div<StyledProps>`
  width: 100%;
  padding: 0 20px;
  margin-bottom: ${props => props.isOpen ? '20px' : '0px'};
  overflow: hidden;
  height: 0;
  transition: height ease 0.2s;
`;

const open = css`
  height: auto;
`;

const overideIcon = css`
  img {
    width: 12px;
    height: 12px;
    top: 2px;
  }
`;

const lineThrough = css`
  text-decoration: line-through;
`;

const Row = styled.div<StyledProps>`
  padding: ${props => props.padding ? props.padding : '0 0 15px'};
  margin: ${props => props.margin ? props.margin : '0px'};
  border-bottom: 1px solid ${props => props.theme.colors.white};
  border-top: 1px solid ${props => props.theme.colors.white};
  border-bottom-width: ${props => props.borderBotWidth ? `${props.borderBotWidth}px` : '0px'};
  border-top-width: ${props => props.borderTopWidth ? `${props.borderTopWidth}px` : '0px'};
  display: flex;
  justify-content: space-between;
`;


const Container = styled.div<StyledProps>`
  background-color: ${props => props.theme.colors.secondary};
  padding: 15px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  @media screen and (min-width: 768px) {
    ${ToggleContainer} {
      max-width: 330px;
    }
  }
`
const paddingRight = css`
  padding-right: 10px;
`;

interface StyledProps {
  theme: ITheme;
  isOpen?: boolean;
  height?: string;
  borderTopWidth?: number;
  borderBotWidth?: number;
  padding?: string;
  margin?: string;
}

interface IValue {
  text: string;
  value: string | number;
  type?: string;
  promotions: { text: string, value: string }[];
  hasDiscount?: boolean
}
export interface PremiumCostProps {
  theme?: ITheme,
  totalPremium: IValue;
  monthlyBenefit: IValue;
  details: IValue[],
  breakdown: IValue[],
  paymentFrequency: IValue,
  onToggle?: (e: any) => void;
}

function PremiumCost({ totalPremium, monthlyBenefit, details, breakdown, paymentFrequency, onToggle }: PremiumCostProps) {
  const theme = useTheme();
  const [totalValue, setTotalValue] = useState<IValue>(totalPremium);
  const [detailsValue, setDetailsValue] = useState<IValue[]>(details);
  const [breakdownValue, setBreakdownValue] = useState<IValue[]>(breakdown);
  const [monthlyBenefitValue, setMonthlyBenefit] = useState<IValue>(monthlyBenefit);
  const [frequencyValue, setFrequency] = useState<IValue>(paymentFrequency);

  const contentRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [height, setHeight] = useState<string>('0px');

  const toggle = () => {
    setHeight(!isOpen ? contentRef.current?.scrollHeight + 'px' : '0px');
    setIsOpen(!isOpen);
    if (onToggle) {
      onToggle(!isOpen);
    }
  }

  useEffect(() => {
    if (!isEqual(totalPremium, totalValue)) {
      setTotalValue(totalPremium);
    }

    if (!isEqual(detailsValue, details)) {
      setDetailsValue(details);
    }

    if (!isEqual(frequencyValue, paymentFrequency)) {
      setFrequency(paymentFrequency);
    }

    if (!isEqual(breakdownValue, breakdown)) {
      setBreakdownValue(breakdown);
    }

    if (!isEqual(monthlyBenefitValue, monthlyBenefit)) {
      setMonthlyBenefit(monthlyBenefit);
    }

  }, [totalPremium, details, breakdown, paymentFrequency, monthlyBenefit, totalValue, detailsValue, breakdownValue, frequencyValue, monthlyBenefitValue]);

  return (
    <Container theme={theme} >
      <TotalContainer theme={theme} isOpen={isOpen}>
        <Heading variant="default" margin="0 10px 0 0" color={theme.colors.white}>
          {Helpers.toCurrency(Number(totalValue.value))}
        </Heading>
        <Text className="title-text" color={theme.colors.white} fontSizeTablet={theme.fontSizeTablet.smallTitle}>{totalValue.text}</Text>
      </TotalContainer>
      <ToggleContainer
        theme={theme}
        isOpen={isOpen}
        className={isOpen ? open : ''}
        ref={contentRef}
        style={{ height }}
      >
        <Row theme={theme} borderBotWidth={1} padding="15px 0" margin={"0 0 15px"}>
          <Text color={theme.colors.white}>{monthlyBenefit.text}</Text>
          <Text fontWeight="bold" color={theme.colors.white}>{monthlyBenefit.value}</Text>
        </Row>
        {detailsValue.map((detail: IValue, index: number) => {
          return (<React.Fragment key={`${index}`}>
            {detail && detail.value && (
              <Row theme={theme} padding={get(detail, 'promotions', []).length > 0 ? '0 0 15px' : '0 0 15px'}>
                <Text className={paddingRight} color={theme.colors.white} fontWeight={get(detail, 'type', '') === 'total' ? 'bold' : 'normal'}>{detail?.text}</Text>
                <Text className={detail?.hasDiscount ? lineThrough : ''} fontWeight="bold" color={theme.colors.white}>{detail?.value}</Text>
              </Row>
            )}
            {get(detail, 'promotions', []).map((promotion: any, i: number) => (
              <Row key={`${index}-${i}`} theme={theme} padding={i === detail.promotions.length - 1 ? '0 0 15px' : '0px'}>
                <Text color={theme.colors.white}>{get(promotion, 'text' )}</Text>
                <Text fontWeight="bold" color={theme.colors.white}>{get(promotion, 'value')}</Text>
              </Row>))}
          </React.Fragment>);
        })}
        {breakdownValue.map((detail: IValue, index: number) => {
          return (<React.Fragment key={`${index}`}>
            {detail && detail.value && (
              <Row theme={theme} padding={get(detail, 'promotions', []).length > 0 ? '0 0 15px' : '0 0 15px'}>
                <Text color={theme.colors.white} fontWeight={get(detail, 'type', '') === 'total' ? 'bold' : 'normal'}>{detail?.text}</Text>
                <Text className={detail?.hasDiscount ? lineThrough : ''} fontWeight="bold" color={theme.colors.white}>{detail?.value}</Text>
              </Row>
            )}
            {get(detail, 'promotions', []).map((promotion: any, i: number) => (
              <Row key={`${index}-${i}`} theme={theme} padding={i === detail.promotions.length - 1 ? '0 0 15px' : '0px'}>
                <Text color={theme.colors.white}>{get(promotion, 'text' )}</Text>
                <Text fontWeight="bold" color={theme.colors.white}>{get(promotion, 'value')}</Text>
              </Row>))}
          </React.Fragment>);
        })}

        <Row theme={theme} borderTopWidth={1} padding="15px 0" margin={"0px"}>
          <Text fontWeight="bold" color={theme.colors.white}>Premium breakdown</Text>
          <Text fontWeight="bold" color={theme.colors.white}>{paymentFrequency.value}</Text>
        </Row>
        <Row theme={theme} padding={'0 0 15px'}>
          <Text color={theme.colors.white} fontWeight={'normal'}>Paid via CPF MediSave<br/>(No cash required)</Text>
          <Text fontWeight="bold" color={theme.colors.white}>{Number(totalValue.value) > 600 ? Helpers.toCurrency(600) : Helpers.toCurrency(Number(totalValue.value))}</Text>
        </Row>
        <Row theme={theme} borderBotWidth={1} padding={'0 0 15px'}>
          <Text color={theme.colors.white} fontWeight={'normal'}>Paid via Credit Card</Text>
          <Text fontWeight="bold" color={theme.colors.white}>{Number(totalValue.value) > 600 ? Helpers.toCurrency(Number(totalValue.value)-600) : Helpers.toCurrency(0)}</Text>
        </Row>
      </ToggleContainer>
      <Link
        onClick={toggle}
        variant="button"
        fontWeight="normal"
        text="Premium Breakdown"
        icon={isOpen ? IconUp : IconDown}
        slotIcon="end"
        color={theme.colors.white}
        underlineStyle="dotted"
        className={overideIcon}
      />
    </Container>
  );
};

PremiumCost.defaultProps = {
  totalPremium: { text: 'total annual premium', value: 'S$0' },
  monthlyBenefit: { text: '', value: '' },
  details: [],
  breakdown: [],
  paymentFrequency: { text: '', value: '' }
}

export default PremiumCost;
